import React, { useState, useEffect, useRef } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Box, 
  Menu, 
  MenuItem, 
  IconButton,
  useMediaQuery,
  useTheme,
  Container,
  Typography,
  Collapse,
  styled
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import Button from './Button';
import logo from '../Media/logo.png';

// Styled components for the mobile menu
const FullScreenOverlay = styled(motion.div)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'linear-gradient(135deg, #ffffff 0%, #f5f9ff 100%)',
  zIndex: 1200,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 0 30px rgba(0, 145, 174, 0.10)',
  padding: '0',
}));

const MenuItemStyled = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 2),
  marginBottom: theme.spacing(1.5),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  borderRadius: '14px',
  background: 'rgba(0,145,174,0.04)',
  boxShadow: '0 1px 4px rgba(0,145,174,0.04)',
  transition: 'all 0.2s cubic-bezier(.4,2,.6,1)',
  fontWeight: 600,
  fontSize: '1.1rem',
  color: '#222',
  textDecoration: 'none',
  '&:hover': {
    background: 'rgba(0,145,174,0.09)',
    color: '#0091AE',
    transform: 'translateX(5px)',
    textDecoration: 'none',
  },
}));

const SubMenuItemStyled = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2, 1.5, 4),
  margin: theme.spacing(0, 2, 1, 4),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  borderRadius: '10px',
  background: 'rgba(0,145,174,0.02)',
  fontWeight: 500,
  fontSize: '1rem',
  color: '#444',
  textDecoration: 'none',
  transition: 'all 0.2s cubic-bezier(.4,2,.6,1)',
  position: 'relative',
  '&:hover': {
    background: 'rgba(0,145,174,0.07)',
    color: '#0091AE',
    transform: 'translateX(5px)',
    textDecoration: 'none',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    left: '-16px',
    top: '50%',
    width: '12px',
    height: '2px',
    backgroundColor: '#0091AE',
    transform: 'translateY(-50%)'
  }
}));

const MenuDivider = styled(Box)(({ theme }) => ({
  height: '3px',
  background: 'linear-gradient(90deg, #64B5F6 0%, rgba(100, 181, 246, 0.2) 100%)',
  margin: theme.spacing(2, 0, 3, 0),
  borderRadius: '10px'
}));

const CustomButton = styled(motion.button)(({ isPrimary }) => ({
  padding: '16px 24px',
  borderRadius: '50px',
  border: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '1rem',
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  marginBottom: '12px',
  width:'80%',
  marginLeft:'10%',
  marginBottom:10,
  background: isPrimary ? 
    'linear-gradient(135deg, #005E83 0%, #0288D1 100%)' : 
    'white',
  color: isPrimary ? 'white' : '#005E83',
  boxShadow: isPrimary ? 
    '0 4px 15px rgba(0, 94, 131, 0.3)' : 
    '0 2px 10px rgba(0, 0, 0, 0.08)',
  border: isPrimary ? 'none' : '2px solid #E1F5FE',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: isPrimary ? 
      '0 6px 20px rgba(0, 94, 131, 0.4)' : 
      '0 4px 15px rgba(0, 0, 0, 0.12)'
  }
}));

const Navbar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isVerySmall = useMediaQuery('(max-width:300px)');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesAnchorEl, setServicesAnchorEl] = useState(null);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const navRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Lock body scroll when mobile menu is open
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const handleServicesClick = (event) => {
    setServicesAnchorEl(event.currentTarget);
  };

  const handleServicesClose = () => {
    setServicesAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMobileServicesClick = () => {
    setMobileServicesOpen(!mobileServicesOpen);
  };

  const serviceOptions = [
    {
      title: 'Residential Mortgages',
      url: '/Services',
      ariaLabel: 'Learn about our residential mortgage solutions'
    },
    {
      title: 'Self-Employed Mortgages',
      url: '/Services',
      ariaLabel: 'Explore mortgage options for self-employed individuals'
    },
    {
      title: 'Construction Mortgages',
      url: '/Services',
      ariaLabel: 'Discover our construction and renovation mortgage options'
    }
  ];

  const menuVariants = {
    closed: {
      opacity: 0,
      x: "-100%",
      transition: {
        x: { stiffness: 1000 }
      }
    },
    open: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    show: { 
      opacity: 1, 
      x: 0,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  const buttonVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { 
      opacity: 1, 
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  return (
    <>
      <AppBar 
        position="fixed" 
        elevation={0}
        sx={{ 
          bgcolor: isScrolled ? 'rgba(255, 255, 255, 0.85)' : 'white',
          borderBottom: '1px solid #eaeaea',
          top: 0,
          zIndex: 1100,
          width: '100%',
          backdropFilter: isScrolled ? 'blur(10px)' : 'none',
          transition: 'all 0.3s ease-in-out'
        }}
        ref={navRef}
      >
        <Container 
          maxWidth="xl" 
          sx={{ 
            pr: { xs: '16px !important', md: '24px !important' }
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between', py: 1, px: '0 !important' }}>
            {/* Logo */}
            <Box
              component={RouterLink}
              to="/"
              aria-label="Return to Mortgage Line homepage"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                '&:focus-visible': {
                  outline: '2px solid #005E83',
                  outlineOffset: '4px',
                  borderRadius: '4px'
                }
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="Mortgage Line Logo"
                sx={{
                  height: { xs: '40px', sm: '50px' },
                  width: 'auto'
                }}
              />
            </Box>

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Box
                  component={RouterLink}
                  to="/"
                  aria-label="Visit our homepage"
                  sx={{
                    color: '#005E83',
                    textDecoration: 'none',
                    fontWeight: 500,
                    '&:hover': { color: '#0288D1' },
                    '&:focus-visible': {
                      outline: '2px solid #005E83',
                      outlineOffset: '4px',
                      borderRadius: '4px'
                    }
                  }}
                >
                  Home
                </Box>

                <Box
                  onClick={handleServicesClick}
                  sx={{
                    color: '#005E83',
                    cursor: 'pointer',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    '&:hover': { color: '#0288D1' },
                    '&:focus-visible': {
                      outline: '2px solid #005E83',
                      outlineOffset: '4px',
                      borderRadius: '4px'
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-haspopup="true"
                  aria-expanded={Boolean(servicesAnchorEl)}
                  aria-label="View our mortgage services"
                >
                  Services
                  <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />
                </Box>

                <Box
                  component={RouterLink}
                  to="/About"
                  aria-label="Learn more about Mortgage Line"
                  sx={{
                    color: '#005E83',
                    textDecoration: 'none',
                    fontWeight: 500,
                    '&:hover': { color: '#0288D1' },
                    '&:focus-visible': {
                      outline: '2px solid #005E83',
                      outlineOffset: '4px',
                      borderRadius: '4px'
                    }
                  }}
                >
                  About
                </Box>

                <Box
                  component={RouterLink}
                  to="/Contact"
                  aria-label="Contact Mortgage Line for assistance"
                  sx={{
                    color: '#005E83',
                    textDecoration: 'none',
                    fontWeight: 500,
                    '&:hover': { color: '#0288D1' },
                    '&:focus-visible': {
                      outline: '2px solid #005E83',
                      outlineOffset: '4px',
                      borderRadius: '4px'
                    }
                  }}
                >
                  Contact
                </Box>

                <Button
                  component={motion.a}
                  href="mailto:rahim@rahimvirani.ca"
                  aria-label="Get a free mortgage consultation"
                  sx={{
                    '&:focus-visible': {
                      outline: '2px solid #005E83',
                      outlineOffset: '4px',
                      borderRadius: '50px'
                    }
                  }}
                >
                  Get a Quote
                </Button>
              </Box>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label={mobileOpen ? "Close menu" : "Open menu"}
                onClick={handleDrawerToggle}
                edge="start"
                sx={{
                  color: '#005E83',
                  '&:focus-visible': {
                    outline: '2px solid #005E83',
                    outlineOffset: '4px',
                    borderRadius: '4px'
                  }
                }}
              >
                {mobileOpen ? <CloseIcon /> : <MenuIcon />}
              </IconButton>
            )}
          </Toolbar>
        </Container>

        {/* Services Dropdown Menu */}
        <Menu
          anchorEl={servicesAnchorEl}
          open={Boolean(servicesAnchorEl)}
          onClose={handleServicesClose}
          disableScrollLock
          PaperProps={{
            elevation: 0,
            sx: {
              mt: 1.5,
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              overflow: 'visible',
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {serviceOptions.map((option) => (
            <MenuItem
              key={option.title}
              component={RouterLink}
              to={option.url}
              onClick={handleServicesClose}
              aria-label={option.ariaLabel}
              sx={{
                py: 1.5,
                px: 2,
                '&:hover': {
                  bgcolor: '#f5f9ff',
                },
                '&:focus-visible': {
                  outline: '2px solid #005E83',
                  outlineOffset: '-2px'
                }
              }}
            >
              {option.title}
            </MenuItem>
          ))}
        </Menu>
      </AppBar>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <FullScreenOverlay
            initial="closed"
            animate="open"
            exit="closed"
            variants={menuVariants}
          >
            <IconButton
              onClick={handleDrawerToggle}
              sx={{
                position: 'absolute',
                top: 18,
                right: 18,
                zIndex: 1300,
                color: '#0091AE',
                bgcolor: 'rgba(0, 145, 174, 0.08)',
                '&:hover': { bgcolor: 'rgba(0, 145, 174, 0.18)' }
              }}
              aria-label="Close menu"
            >
              <CloseIcon />
            </IconButton>
            <Box
              component={motion.div}
              variants={containerVariants}
              initial="hidden"
              animate="show"
              sx={{
                p: 3,
                pt: { xs: 8, sm: 10 },
                maxWidth: 600,
                mx: 'auto',
                width: '100%'
              }}
            >
              <Box
                component={motion.div}
                variants={itemVariants}
                sx={{ mb: 4 }}
              >
                <Box
                  component={RouterLink}
                  to="/"
                  onClick={handleDrawerToggle}
                  aria-label="Return to Mortgage Line homepage"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    textDecoration: 'none',
                    '&:focus-visible': {
                      outline: '2px solid #005E83',
                      outlineOffset: '4px',
                      borderRadius: '4px'
                    }
                  }}
                >
                  <Box
                    component="img"
                    src={logo}
                    alt="Mortgage Line Logo"
                    sx={{
                      height: { xs: '40px', sm: '50px' },
                      width: 'auto'
                    }}
                  />
                </Box>
              </Box>

              <MenuItemStyled
                component={RouterLink}
                to="/"
                onClick={handleDrawerToggle}
                aria-label="Visit our homepage"
                sx={{
                  '&:focus-visible': {
                    outline: '2px solid #005E83',
                    outlineOffset: '4px',
                    borderRadius: '12px'
                  }
                }}
              >
                <Typography variant="h6">Home</Typography>
                <ArrowRightAltIcon />
              </MenuItemStyled>

              <MenuItemStyled
                onClick={handleMobileServicesClick}
                role="button"
                tabIndex={0}
                aria-expanded={mobileServicesOpen}
                aria-label="View our mortgage services"
                sx={{
                  '&:focus-visible': {
                    outline: '2px solid #005E83',
                    outlineOffset: '4px',
                    borderRadius: '12px'
                  }
                }}
              >
                <Typography variant="h6">Services</Typography>
                <KeyboardArrowDownIcon 
                  sx={{ 
                    transform: mobileServicesOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.3s ease'
                  }} 
                />
              </MenuItemStyled>

              <Collapse in={mobileServicesOpen}>
                {serviceOptions.map((option) => (
                  <SubMenuItemStyled
                    key={option.title}
                    component={RouterLink}
                    to={option.url}
                    onClick={handleDrawerToggle}
                    aria-label={option.ariaLabel}
                    sx={{
                      '&:focus-visible': {
                        outline: '2px solid #005E83',
                        outlineOffset: '4px',
                        borderRadius: '12px'
                      }
                    }}
                  >
                    <Typography variant="body1">{option.title}</Typography>
                    <ArrowRightAltIcon />
                  </SubMenuItemStyled>
                ))}
              </Collapse>

              <MenuItemStyled
                component={RouterLink}
                to="/About"
                onClick={handleDrawerToggle}
                aria-label="Learn more about Mortgage Line"
                sx={{
                  '&:focus-visible': {
                    outline: '2px solid #005E83',
                    outlineOffset: '4px',
                    borderRadius: '12px'
                  }
                }}
              >
                <Typography variant="h6">About</Typography>
                <ArrowRightAltIcon />
              </MenuItemStyled>

              <MenuItemStyled
                component={RouterLink}
                to="/Contact"
                onClick={handleDrawerToggle}
                aria-label="Contact Mortgage Line for assistance"
                sx={{
                  '&:focus-visible': {
                    outline: '2px solid #005E83',
                    outlineOffset: '4px',
                    borderRadius: '12px'
                  }
                }}
              >
                <Typography variant="h6">Contact</Typography>
                <ArrowRightAltIcon />
              </MenuItemStyled>

              <MenuDivider />

              <Box
                component={motion.div}
                variants={buttonVariants}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  mt: 2
                }}
              >
                <CustomButton
                  component={motion.a}
                  href="mailto:rahim@rahimvirani.ca"
                  onClick={handleDrawerToggle}
                  isPrimary={true}
                  aria-label="Get a free mortgage consultation"
                  sx={{
                    '&:focus-visible': {
                      outline: '2px solid #005E83',
                      outlineOffset: '4px',
                      borderRadius: '50px'
                    }
                  }}
                >
                  Get a Quote
                </CustomButton>
              </Box>
            </Box>
          </FullScreenOverlay>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar; 
